import React from 'react';

import PagingComponent, { PagedState } from '../utils/paging';
import ActivityLogCell from './ActivityLogCell';
import { ManagerNickName } from '../managers/managers';
import { VendorNickName, VendorUserName } from '../vendors/vendors';
import { AuthState } from '../login/auth';
import FeatureButton from '../utils/FeatureButton';
import { SORT_OPTIONS } from '../utils/hooks/useSort';
import { ManagerPermission } from '../permission-profiles/permission';
import {
    defaultPartnerSortOptions,
    defaultPartnerUserSortOptions,
    BeneficiarySupportOrganizationName,
    BeneficiarySupportUserName,
} from '../partners/partners';
import { ActivityLog } from './activityLogs';

/** Coordination / beneficiary-support deployments do not use the vendor module. */
const EMPTY_VENDOR_NICK_NAMES: VendorNickName[] = [];
const EMPTY_VENDOR_USER_NAMES: VendorUserName[] = [];

/** Beneficiary support org (Redux still uses "partner" naming). */
function activityLogsNeedBeneficiarySupportOrganizationNames(logs: ActivityLog<unknown>[]): boolean {
    return logs.some((log) => !!log.partnerId);
}

function activityLogsNeedBeneficiarySupportUserNames(logs: ActivityLog<unknown>[]): boolean {
    return logs.some((log) => {
        const payload = log.payload as { partnerUserId?: string | number } | undefined;
        return !!payload?.partnerUserId;
    });
}

interface Props {
    loadActivityLogs: (page: number, limit: number, managerId?: string) => void;
    activityLogs: PagedState<ActivityLog<unknown>>;
    managersNickNames: ManagerNickName[];
    isManager: boolean;
    entityId: string;
    url?: string;
    loadManagersNickNames: () => void;
    loadPartnersNames: (sortOptions: SORT_OPTIONS) => void;
    loadPartnerUserNames: (sortOptions: SORT_OPTIONS) => void;
    auth: AuthState;
    downloadRestriction: ManagerPermission[];
    partnersNickNames: Array<BeneficiarySupportOrganizationName>;
    partnerUsersNickNames: Array<BeneficiarySupportUserName>;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface State {}

const PageLimit = 25;

export class ActivityLogsList extends React.Component<Props, State> {
    private beneficiarySupportOrgNamesLoadRequested = false;

    private beneficiarySupportUserNamesLoadRequested = false;

    componentDidMount() {
        this.props.loadManagersNickNames();
        this.onChangePageRequested(1);
        this.requestActivityLogReferenceListsIfNeeded(this.props.activityLogs.items);
    }

    componentDidUpdate(prevProps: Props) {
        if (this.activityLogItemsChanged(prevProps.activityLogs.items, this.props.activityLogs.items)) {
            this.requestActivityLogReferenceListsIfNeeded(this.props.activityLogs.items);
        }
    }

    private activityLogItemsChanged(
        prevItems: ActivityLog<unknown>[] | undefined,
        items: ActivityLog<unknown>[] | undefined
    ): boolean {
        if (items === prevItems) {
            return false;
        }
        if (!items?.length && !prevItems?.length) {
            return false;
        }
        if (!items?.length || !prevItems?.length) {
            return true;
        }
        if (items.length !== prevItems.length) {
            return true;
        }
        return items.some((item, index) => item.id !== prevItems[index]?.id);
    }

    private requestActivityLogReferenceListsIfNeeded(logs: ActivityLog<unknown>[] | undefined) {
        if (!logs?.length) {
            return;
        }
        if (
            !this.beneficiarySupportOrgNamesLoadRequested &&
            activityLogsNeedBeneficiarySupportOrganizationNames(logs)
        ) {
            this.beneficiarySupportOrgNamesLoadRequested = true;
            this.props.loadPartnersNames(defaultPartnerSortOptions);
        }
        if (!this.beneficiarySupportUserNamesLoadRequested && activityLogsNeedBeneficiarySupportUserNames(logs)) {
            this.beneficiarySupportUserNamesLoadRequested = true;
            this.props.loadPartnerUserNames(defaultPartnerUserSortOptions);
        }
    }

    onChangePageRequested(page: number) {
        if (this.props.isManager) {
            this.props.loadActivityLogs(page, PageLimit, this.props.auth.manager.id);
        } else {
            this.props.loadActivityLogs(page, PageLimit);
        }
    }

    checkIfEntitiesListsAreFetched() {
        return this.props.managersNickNames && this.props.managersNickNames.length > 0;
    }

    render() {
        const manager = this.props.auth.manager;
        const downloadRestriction = this.props.downloadRestriction;

        return (
            <div>
                <div className="row">
                    <h6 className="col-sm-4">Activity Logs</h6>

                    {this.props.url && (
                        <div className="col-sm-8 tr">
                            <FeatureButton
                                linkTo={this.props.url}
                                manager={manager}
                                restrictedToFeatures={downloadRestriction}
                                state={{ entityId: this.props.entityId }}
                                title={'Download'}
                            />
                        </div>
                    )}
                </div>

                {this.checkIfEntitiesListsAreFetched() &&
                    this.props.activityLogs.items &&
                    this.props.activityLogs.items.length > 0 &&
                    this.props.activityLogs.items.map((log) => (
                        <ActivityLogCell
                            key={log.id}
                            log={log}
                            managersNickName={this.props.managersNickNames}
                            partnerUsersNames={this.props.partnerUsersNickNames}
                            partnersNames={this.props.partnersNickNames}
                            vendorUsersNames={EMPTY_VENDOR_USER_NAMES}
                            vendorsNickNames={EMPTY_VENDOR_NICK_NAMES}
                        />
                    ))}
                <PagingComponent
                    onPageChanged={this.onChangePageRequested.bind(this)}
                    paging={this.props.activityLogs.paging}
                />
            </div>
        );
    }
}
